import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Download, Factory, X } from 'lucide-react';
import { propsFundoModal } from '../../../src/lib/backdropModal';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useToast } from '../../../src/contexts/ToastContext';
import { logUsageEvent } from '../../../src/lib/logger';
import { RateioAjusteUc } from '../../../types';
import FiltrosSaldosBar from './FiltrosSaldosBar';
import KpisSaldos from './KpisSaldos';
import TabelaSaldosUcs from './TabelaSaldosUcs';
import {
  ajusteAberto,
  contarIndicadores,
  filtrarEOrdenar,
  FILTROS_PADRAO,
  formatarKwh,
  formatarPercentRateio,
  type FiltrosSaldos,
  type LinhaSaldoUc,
  type ResumoGeradora,
} from './saldosUcs';
import { exportarSaldosUcs } from './saldosUcsExportacao';
import { nomeCurtoUsina } from './saldosUcsVisual';

/** Chip das UCs que a planilha trouxe sem UC geradora (canceladas, na maioria). */
const SEM_USINA = '__sem_usina__';

interface UsinaUcsModalProps {
  resumo: ResumoGeradora[];
  linhas: LinhaSaldoUc[];
  /** `null` abre com todas as UCs do rateio. */
  usinaInicial: string | null;
  filtrosIniciais?: Partial<FiltrosSaldos>;
  mesRateio: string;
  ajustesDaLinha: (l: LinhaSaldoUc) => RateioAjusteUc[];
  onAbrirUc: (l: LinhaSaldoUc) => void;
  onAjustar: (l: LinhaSaldoUc) => void;
  onClose: () => void;
}

const nomeDaUsina = (r: ResumoGeradora | undefined, numero: string) => (r?.ugNome ? nomeCurtoUsina(r.ugNome) : numero);

/**
 * UCs de uma ou mais usinas, num modal no centro da tela. Os chips de cima somam
 * ou tiram usinas, os indicadores filtram por saldo, e clicar numa UC abre o
 * detalhe dela por cima deste modal.
 */
const UsinaUcsModal: React.FC<UsinaUcsModalProps> = ({
  resumo,
  linhas,
  usinaInicial,
  filtrosIniciais,
  mesRateio,
  ajustesDaLinha,
  onAbrirUc,
  onAjustar,
  onClose,
}) => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [usinas, setUsinas] = useState<string[]>(usinaInicial ? [usinaInicial] : []);
  const [filtros, setFiltros] = useState<FiltrosSaldos>({ ...FILTROS_PADRAO, ...filtrosIniciais });
  const mudarFiltros = (m: Partial<FiltrosSaldos>) => setFiltros(f => ({ ...f, ...m }));

  const alternarUsina = (numero: string) =>
    setUsinas(atual => (atual.includes(numero) ? atual.filter(n => n !== numero) : [...atual, numero]));

  const daSelecao = useMemo(
    () =>
      usinas.length === 0
        ? linhas
        : linhas.filter(l => (l.ugNumero ? usinas.includes(l.ugNumero) : usinas.includes(SEM_USINA))),
    [linhas, usinas]
  );
  const indicadores = useMemo(() => contarIndicadores(daSelecao), [daSelecao]);
  const filtradas = useMemo(
    () => filtrarEOrdenar(daSelecao, filtros, l => ajustesDaLinha(l).some(ajusteAberto)),
    [daSelecao, filtros, ajustesDaLinha]
  );

  const unica = usinas.length === 1 ? resumo.find(r => r.ugNumero === usinas[0]) : undefined;
  const titulo =
    usinas.length === 0
      ? 'Todas as UCs do rateio'
      : usinas.length === 1
        ? usinas[0] === SEM_USINA ? 'UCs sem usina na planilha' : unica?.ugNome || usinas[0]
        : `${usinas.length} usinas selecionadas`;

  // Total de UCs de cada chip (todas as linhas, não só as que recebem).
  const ucsPorUsina = useMemo(() => {
    const m = new Map<string, number>();
    for (const l of linhas) {
      const chave = l.ugNumero || SEM_USINA;
      m.set(chave, (m.get(chave) || 0) + 1);
    }
    return m;
  }, [linhas]);

  const exportar = () => {
    const doResumo = usinas.length === 0 ? resumo : resumo.filter(r => usinas.includes(r.ugNumero));
    exportarSaldosUcs(filtradas, doResumo, ajustesDaLinha, mesRateio);
    logUsageEvent({ userId: user?.id, modulo: 'gestao', funcionalidade: 'rateio_saldos', acao: 'exportar', origem: 'acao' });
    addToast(`${filtradas.length} UC(s) exportada(s).`, 'success');
  };

  const chip = (ativo: boolean) =>
    `flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
      ativo
        ? 'bg-primary/10 border-primary/40 text-primary'
        : 'bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800'
    }`;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 bg-black/50 backdrop-blur-sm animate-fade-in" {...propsFundoModal(onClose)}>
      <div
        className="bg-gray-50 dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-[1680px] h-[96vh] sm:h-[92vh] flex flex-col overflow-hidden animate-slide-up"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-label={titulo}
      >
        {/* Cabeçalho, usinas, indicadores e filtros: nada disto rola. */}
        <div className="flex-shrink-0">
          <div className="flex items-start justify-between gap-3 px-5 pt-4">
            <div className="min-w-0">
              <h2 className="flex items-center gap-2 text-lg font-bold text-gray-900 dark:text-white font-poppins truncate">
                <Factory size={18} className="text-primary flex-shrink-0" /> {titulo}
              </h2>
              {unica ? (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex flex-wrap gap-x-4 gap-y-1 tabular-nums">
                  <span>{unica.ugNumero}</span>
                  <span>
                    Geração média{' '}
                    <strong className="text-gray-700 dark:text-gray-200">
                      {unica.mediaGeracaoKwh != null ? `${formatarKwh(unica.mediaGeracaoKwh)} kWh` : 'não registrada'}
                    </strong>
                    {unica.mesesGeracao ? ` (${unica.mesesGeracao} ${unica.mesesGeracao === 1 ? 'mês' : 'meses'})` : ''}
                  </span>
                  <span>
                    Alocado <strong className="text-gray-700 dark:text-gray-200">{formatarPercentRateio(unica.somaVigente)}</strong> · sobra{' '}
                    {formatarPercentRateio(Math.max(0, 1 - unica.somaVigente))}
                  </span>
                  <span>
                    Pelo modelo <strong className="text-gray-700 dark:text-gray-200">{formatarPercentRateio(unica.somaSugerida)}</strong>
                  </span>
                </p>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Rateio de {mesRateio}. Clique nas usinas abaixo para somar ou tirar da lista.</p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={exportar}
                disabled={filtradas.length === 0}
                title="Exporta as UCs que estão na lista agora"
                className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-200 rounded-xl text-sm font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 disabled:opacity-50"
              >
                <Download size={15} /> <span className="hidden sm:inline">Exportar</span>
              </button>
              <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full" aria-label="Fechar">
                <X size={18} />
              </button>
            </div>
          </div>

          {/* No computador as usinas quebram linha para aparecerem todas; no celular rolam de lado. */}
          <div className="flex gap-2 overflow-x-auto sm:flex-wrap sm:overflow-visible px-5 py-3" role="group" aria-label="Usinas">
            <button onClick={() => setUsinas([])} aria-pressed={usinas.length === 0} className={chip(usinas.length === 0)}>
              Todas <span className="tabular-nums opacity-70">{linhas.length}</span>
            </button>
            {resumo.map(r => (
              <button
                key={r.ugNumero}
                onClick={() => alternarUsina(r.ugNumero)}
                aria-pressed={usinas.includes(r.ugNumero)}
                title={`${r.ugNome || 'Usina sem cadastro'} · ${r.ugNumero}`}
                className={chip(usinas.includes(r.ugNumero))}
              >
                {nomeDaUsina(r, r.ugNumero)}
                <span className="tabular-nums opacity-70">{ucsPorUsina.get(r.ugNumero) || 0}</span>
              </button>
            ))}
            {ucsPorUsina.get(SEM_USINA) ? (
              <button
                onClick={() => alternarUsina(SEM_USINA)}
                aria-pressed={usinas.includes(SEM_USINA)}
                title="UCs que a planilha do rateio trouxe sem UC geradora"
                className={chip(usinas.includes(SEM_USINA))}
              >
                Sem usina
                <span className="tabular-nums opacity-70">{ucsPorUsina.get(SEM_USINA)}</span>
              </button>
            ) : null}
          </div>

          <div className="px-5 pb-3">
            <KpisSaldos indicadores={indicadores} filtros={filtros} setFiltros={setFiltros} />
          </div>
        </div>

        <div className="flex-1 min-h-0 flex flex-col mx-5 mb-5 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
          <div className="flex-shrink-0 p-4 border-b border-gray-100 dark:border-gray-700">
            <FiltrosSaldosBar filtros={filtros} onChange={mudarFiltros} />
          </div>
          <TabelaSaldosUcs
            linhas={filtradas}
            mesRateio={mesRateio}
            ajustesDaLinha={ajustesDaLinha}
            onAbrir={onAbrirUc}
            onAjustar={onAjustar}
            chaveReinicio={`${mesRateio}|${usinas.join(',')}|${JSON.stringify(filtros)}`}
          />
        </div>
      </div>
    </div>,
    document.body
  );
};

export default UsinaUcsModal;
